import { FC, Fragment } from "react";
import {
    Flex,
    Heading,
    Text,
    Grid,
    GridItem,
    HorizontalScroll,
    Box,
    Spacer,
} from "@smile2impress/components/relaunch";

import { CtaButton } from "components/CtaButton";

import { MultiColumnProps } from "./MultiColumn.types";
import { Column } from "./components";

const MIN_ITEMS_LENGTH = 4;

export const MultiColumn: FC<MultiColumnProps> = ({
    content,
    headingAs,
    itemComponent,
}) => {
    const {
        title,
        subtitle,
        items,
        cta,
        mobileSlider = false,
        mobileGrid = false,
        narrowColumns = false,
        doNotStretch = false,
        ignoreMinimumGrid = true,
        minimumGridSize = MIN_ITEMS_LENGTH,
        gridRows,
        mobileGridRows,
        gridRowGap,
        itemContent,
        gridSpacing = "56px",
    } = content;

    const gridColumnsCount = gridRows
        ? Math.ceil(items.length / gridRows)
        : items.length;
    const columnsCount = ignoreMinimumGrid
        ? gridColumnsCount
        : Math.max(gridColumnsCount, minimumGridSize);

    const shortList = items.length < minimumGridSize;
    const gridSizing = gridRows
        ? `repeat(${columnsCount}, 1fr)`
        : new Array(
              ignoreMinimumGrid
                  ? items.length
                  : shortList
                  ? minimumGridSize
                  : items.length
          )
              .fill("2fr")
              .join(` ${gridSpacing} `);

    const Component = itemComponent || Column;
    type ComponentProps = React.ComponentProps<typeof Component>;

    const renderMobileSlider = () => (
        <Flex direction="row">
            <HorizontalScroll>
                <Flex
                    gap={[1]}
                    alignItems={doNotStretch ? "flex-start" : "stretch"}
                    pl={2}
                >
                    {items.map((item, index) => (
                        <Flex
                            minWidth={narrowColumns ? "227px" : 8}
                            key={`${item.title}-${index}`}
                        >
                            <Component
                                {...(item as ComponentProps)}
                                {...itemContent}
                            />
                        </Flex>
                    ))}
                    {/* hack to create a gap on the right */}
                    <Spacer minW="0.1px" />
                </Flex>
            </HorizontalScroll>
        </Flex>
    );

    const renderMobileGrid = () => (
        <Grid
            gridTemplateColumns="1fr 1fr"
            gridTemplateRows="1fr 1fr"
            gridColumnGap="8px"
            gridRowGap="10px"
        >
            {items.map((item, index) => (
                <GridItem key={`${item.title}-${index}`}>
                    <Component {...(item as ComponentProps)} {...itemContent} />
                </GridItem>
            ))}
        </Grid>
    );

    return (
        <Flex direction="column" mx={mobileGrid ? 0 : [-2, -2, 0]}>
            {title && (
                <Grid
                    px={[2, 2, 0]}
                    gridTemplateColumns={[
                        "repeat(6, 1fr)",
                        "repeat(6, 1fr)",
                        "repeat(24, 1fr)",
                    ]}
                    gridColumnGap={[2]}
                >
                    <GridItem colStart={[1, 1, 2]} colEnd={[7, 7, 24]}>
                        <Flex
                            direction="row"
                            justifyContent="space-between"
                            mb={[3, 3, 4]}
                        >
                            <Flex
                                direction="column"
                                maxW={["100%", "100%", "65%"]}
                            >
                                <Heading
                                    maxW="800px"
                                    textStyle={["title1"]}
                                    color="text.base"
                                    as={headingAs}
                                >
                                    {title}
                                </Heading>
                                {subtitle && (
                                    <Text
                                        maxW="800px"
                                        mt={[1]}
                                        textStyle={["body"]}
                                        color="text.base"
                                    >
                                        {subtitle}
                                    </Text>
                                )}
                            </Flex>
                            {cta && (
                                <Box
                                    alignSelf="flex-end"
                                    display={["none", "none", "block"]}
                                >
                                    <CtaButton {...cta} />
                                </Box>
                            )}
                        </Flex>
                    </GridItem>
                </Grid>
            )}

            <Box display={["block", "block", "none"]}>
                {mobileGridRows ? (
                    <Box px={2}>
                        <Grid
                            gridTemplateColumns={`repeat(${Math.ceil(
                                items.length / mobileGridRows
                            )}, 1fr)`}
                            gridColumnGap={3}
                            gridRowGap={3}
                        >
                            {items.map((item, index) => (
                                <GridItem key={`${item.title}-${index}`}>
                                    <Component
                                        {...(item as ComponentProps)}
                                        {...itemContent}
                                    />
                                </GridItem>
                            ))}
                        </Grid>
                    </Box>
                ) : mobileSlider ? (
                    renderMobileSlider()
                ) : mobileGrid ? (
                    renderMobileGrid()
                ) : gridRows ? (
                    <Box px={2}>
                        <Grid
                            gridTemplateColumns={`repeat(${Math.ceil(
                                items.length / gridRows
                            )}, 1fr)`}
                            gridColumnGap={3}
                            gridRowGap={3}
                        >
                            {items.map((item, index) => (
                                <GridItem key={`${item.title}-${index}`}>
                                    <Component
                                        {...(item as ComponentProps)}
                                        {...itemContent}
                                    />
                                </GridItem>
                            ))}
                        </Grid>
                    </Box>
                ) : (
                    <Flex direction="column" px={2} gap={[3, 3, 0]}>
                        {items.map((item, index) => (
                            <Flex key={`${item.title}-${index}`}>
                                <Component
                                    {...(item as ComponentProps)}
                                    {...itemContent}
                                />
                            </Flex>
                        ))}
                    </Flex>
                )}
            </Box>
            <Box mx={[-2, -2, 0]} display={["none", "none", "block"]}>
                <Grid
                    gridTemplateColumns={"repeat(24, 1fr)"}
                    gridColumnGap={[2]}
                >
                    <GridItem colStart={2} colEnd={24}>
                        <Grid
                            gridTemplateColumns={gridSizing}
                            {...(gridRows && {
                                gridColumnGap: gridSpacing,
                                gridRowGap: gridRowGap,
                            })}
                        >
                            <>
                                {items.map((item, index) => (
                                    <Fragment key={`${item.title}-${index}`}>
                                        <GridItem>
                                            <Component
                                                {...(item as ComponentProps)}
                                                {...itemContent}
                                            />
                                        </GridItem>
                                        {!gridRows &&
                                            items.length - index >= 2 && (
                                                <GridItem />
                                            )}
                                    </Fragment>
                                ))}
                            </>
                        </Grid>
                    </GridItem>
                </Grid>
            </Box>

            {cta && (
                <Box mx={[2, 2, 0]} mt={3} display={["block", "block", "none"]}>
                    <CtaButton {...cta} />
                </Box>
            )}
        </Flex>
    );
};
